---
title: Operations
description: Running nights, reading flags, and debugging in the right order.
---

## Running a night

Nights normally run themselves from the schedule. From the Runs page you can
also start any date by hand, or re-run a date that already has results after
fixing settings or calibration. A re-run overwrites that night's report and
artifacts; the staged footage is reused, so it does not re-download.

First run on a new machine: the detector engine is built on that GPU, which
takes about fifteen minutes. This is one-time and normal.

## Reading the quality flags

Every report carries acceptance flags. They are self-checks, not errors:

| Flag                              | What it usually means                                          |
| --------------------------------- | -------------------------------------------------------------- |
| Entries and exits disagree        | Tracker identity trouble, or a counting line on a busy walkway |
| Too many crossings per minute     | Double counting; someone hovering on a line, or identity churn |
| One camera far quieter than peers | Camera bumped, dirty or offline for part of the night          |
| Identities far exceed entries     | One person splitting into many IDs, or the reverse             |

A flagged night still has numbers. The flag says: check before trusting.

## Debug in this order

When counts or dwell look wrong, the cause is almost always identity, not
the counting math. Work top down and stop at the first thing that is broken:

1. **Tracking first.** Watch the debug video (Tracking settings). If one
   person changes ID between cameras, or two people share an ID, tune the
   cross-camera match confidence in small steps. Dwell breaks before counts
   do, so strange dwell numbers are an identity alarm.
2. **Detection second.** If people are missed or phantoms appear, adjust the
   detection confidence. Only consider frame skipping while identity
   retention stays near perfect.
3. **Analytics last.** Only once identities are stable are the analytics
   knobs meaningful. They cannot fix identity problems; they can only hide
   them.

## Calibration

Calibrate after installing cameras, and recalibrate after any physical
contact with one. A bumped camera keeps producing plausible-looking numbers
that are silently wrong, and the quiet-camera flag is often the first
visible symptom. Automatic calibration runs from footage via the Cameras
page; the manual point-matching dialog exists for cameras the automatic run
cannot solve.

## Speed levers, in order

The pipeline is usually decode-bound, not compute-bound. If a night takes too
long, pull these in order, each gated by the one before:

1. Tune tracking until identities are stable. Nothing below is safe before
   this.
2. Switch the detector to fp16, only after the validation script passes on
   that GPU.
3. Skip frames between detections, only while the tracker holds identities
   at 99% or better between consecutive frames.
4. Measure before buying hardware. Profiling tells you whether decode,
   inference or a starving muxer is the actual ceiling.

## When a night is refused

A refusal at the start of the run is the session gates doing their job. The
message names the check that failed: a camera missing, files starting too far
apart, mismatched frame rates, an unsupported codec, or missing recording
timestamps. Fix the recording side and re-run; the pipeline never processes a
night it cannot line up.
